import { v4 as uuid } from "uuid";
import notDeleted from "../notDeleted";

/**
 * @typedef {object} IntakeFormCopyInput
 * @property {object} form - Source `IntakeForms` doc data (may include `docid`).
 * @property {Array<object>} [fields] - Source `IntakeFormFields` docs (`FormId === form.ID`).
 * @property {Array<object>} [options] - Source `MultipleQuestionOptions` docs (`Field === field.ID`).
 * @property {string} client - Tenant CompagnyID to stamp on every copy.
 * @property {*} timestamp - Firestore Timestamp value for the copy's `Timestamp`.
 * @property {string} [userId] - ID of the user creating the copy.
 * @property {number} position - `Position` for the new form (usually list length + 1).
 * @property {() => string} [idFn] - ID generator, injectable for tests (defaults to uuid).
 */

/**
 * @typedef {object} IntakeFormCopyResult
 * @property {object} form - New `IntakeForms` doc data.
 * @property {Array<object>} fields - New `IntakeFormFields` docs.
 * @property {Array<object>} options - New `MultipleQuestionOptions` docs.
 */

/**
 * Build every document needed to duplicate an intake form.
 *
 * Generates fresh IDs and remaps the cross-references (`FormId` on fields,
 * `Field` on options) to the new IDs, so the copy is fully self-contained.
 * Soft-deleted fields/options are dropped — these come from `.get()` bypass
 * reads that the central `useFirestore` soft-delete filter never touched.
 *
 * Pure: performs no Firestore writes. The caller commits the returned docs.
 *
 * @param {IntakeFormCopyInput} input
 * @returns {IntakeFormCopyResult}
 */
const buildIntakeFormCopy = ({
  form,
  fields = [],
  options = [],
  client,
  timestamp,
  userId = "",
  position,
  idFn = uuid,
}) => {
  const newFormID = idFn();

  const { docid: _formDocid, ...formData } = form || {};
  const newForm = {
    ...formData,
    ID: newFormID,
    Title: `${form?.Title || "Naamloos"} (kopie)`,
    CompagnyID: client,
    Compagny: client,
    Position: position,
    Timestamp: timestamp,
    User: userId,
  };

  // old field.ID -> new field.ID, so options can be repointed.
  const fieldIdMap = new Map();
  const newFields = (fields || []).filter(notDeleted).map((field) => {
    const newFieldID = idFn();
    fieldIdMap.set(field.ID, newFieldID);

    const { docid: _fieldDocid, ...fieldData } = field;
    return {
      ...fieldData,
      ID: newFieldID,
      FormId: newFormID,
      CompagnyID: client,
      Compagny: client,
      Timestamp: timestamp,
    };
  });

  const newOptions = (options || [])
    .filter(notDeleted)
    // Skip options whose field was soft-deleted (and thus not copied).
    .filter((opt) => fieldIdMap.has(opt.Field))
    .map((opt) => {
      const { docid: _optDocid, ...optData } = opt;
      return {
        ...optData,
        ID: idFn(),
        Field: fieldIdMap.get(opt.Field),
        CompagnyID: client,
        Compagny: client,
        Timestamp: timestamp,
      };
    });

  return { form: newForm, fields: newFields, options: newOptions };
};

export default buildIntakeFormCopy;
